import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { format } from 'date-fns';
import { toast } from 'sonner';
import {
  FileText, Users, Eye, EyeOff, AlertTriangle, Trash2, XCircle, CheckCircle,
  BarChart3, UserCheck, UserX, Plus, Pencil, Tag, Pin,
  RefreshCw, Download, Radio, Activity, Shield, TrendingUp, Search, ChevronDown,
} from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = (token) => ({ Authorization: `Bearer ${token}`, withCredentials: true });
const ARTICLES_PER_PAGE = 30;

export default function AdminPage() {
  const { token } = useAuth();
  const { t, isHindi } = useLanguage();

  // ── Tab ──
  const [activeTab, setActiveTab] = useState('articles');

  // ── Articles ──
  const [articles, setArticles] = useState([]);
  const [articleTotal, setArticleTotal] = useState(0);
  const [articlePage, setArticlePage] = useState(1);
  const [articleSearch, setArticleSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkAction, setBulkAction] = useState('');
  const [actionDialog, setActionDialog] = useState({ open: false, type: null, articleId: null, label: '' });

  const [reporterDialog, setReporterDialog] = useState({ open: false, type: null, reporter: null });

  // ── Categories ──
  const [categories, setCategories] = useState([]);
  const [categoryForm, setCategoryForm] = useState({ id: '', name: '', name_hi: '', parent_id: '' });
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryDialog, setCategoryDialog] = useState({ open: false, category: null });
  const categoryFormRef = useRef(null);

  // ── Users ──
  const [users, setUsers] = useState([]);
  const [userRoleFilter, setUserRoleFilter] = useState('all');
  const [userDialog, setUserDialog] = useState({ open: false, type: null, user: null });
  const [userForm, setUserForm] = useState({ name: '', email: '', phone: '', role: 'reporter', password: '' });
  const [showTemporaryPassword, setShowTemporaryPassword] = useState(false);

  // ── Breaking News ──
  const [breakingText, setBreakingText] = useState('');
  const [breakingItems, setBreakingItems] = useState([]);
  const [breakingItemsLoading, setBreakingItemsLoading] = useState(true);
  const [breakingArticleOptions, setBreakingArticleOptions] = useState([]);
  const [breakingArticleId, setBreakingArticleId] = useState('');
  const [breakingArticleSearch, setBreakingArticleSearch] = useState('');
  const [breakingArticlePickerOpen, setBreakingArticlePickerOpen] = useState(false);
  const [showBreakingForm, setShowBreakingForm] = useState(false);
  const [tickerLimitAlert, setTickerLimitAlert] = useState(false);
  const [tickerActionDialog, setTickerActionDialog] = useState({ open: false, type: null, id: null, nextActive: null });
  const breakingFormRef = useRef(null);
  const [editingBreakingId, setEditingBreakingId] = useState(null);
  const [editingBreakingText, setEditingBreakingText] = useState('');
  const [editingBreakingArticleId, setEditingBreakingArticleId] = useState('');
  const [editingBreakingArticleSearch, setEditingBreakingArticleSearch] = useState('');
  const [editingBreakingArticlePickerOpen, setEditingBreakingArticlePickerOpen] = useState(false);

  // ── Analytics ──
  const [analytics, setAnalytics] = useState(null);

  // ── Audit Log ──
  const [auditLogs, setAuditLogs] = useState([]);

  // ── Top Stories (ranked automatically; read-only) ──
  const [topStories, setTopStories] = useState(null);
  const [pinnedArticles, setPinnedArticles] = useState([]);
  const [pinPriorityDialog, setPinPriorityDialog] = useState({ open: false, articleId: null, title: '', currentPriority: null, nextPriority: null });

  // ── Shared ──
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const headers = { headers: { Authorization: `Bearer ${token}` }, withCredentials: true };

  // ── Data fetchers ──
  const fetchStats = useCallback(async () => {
    const res = await axios.get(`${API}/admin/stats`, headers);
    setStats(res.data);
  }, [token]);

  const fetchArticles = useCallback(async () => {
    const params = {
      page: articlePage,
      limit: ARTICLES_PER_PAGE,
      ...(filter !== 'all' ? { status: filter } : {}),
      ...(articleSearch.trim() ? { search: articleSearch.trim() } : {}),
    };
    const res = await axios.get(`${API}/admin/articles`, { params, ...headers });
    setArticles(res.data.articles);
    setArticleTotal(res.data.total);
    setSelectedIds([]);
  }, [token, filter, articlePage, articleSearch]);

  const fetchCategories = useCallback(async () => {
    const res = await axios.get(`${API}/categories`, headers);
    setCategories(res.data);
  }, [token]);

  const fetchUsers = useCallback(async () => {
    const params = userRoleFilter !== 'all' ? { role: userRoleFilter } : {};
    const res = await axios.get(`${API}/admin/users`, { params, ...headers });
    setUsers(res.data);
  }, [token, userRoleFilter]);

  const fetchBreakingNews = useCallback(async () => {
    setBreakingItemsLoading(true);
    try {
      const [itemsRes, articlesRes] = await Promise.all([
        axios.get(`${API}/admin/breaking-news/items`, headers),
        axios.get(`${API}/admin/articles`, { params: { status: 'published', limit: 1000 }, ...headers }),
      ]);
      setBreakingItems(itemsRes.data);
      setBreakingArticleOptions(articlesRes.data.articles);
    } finally {
      setBreakingItemsLoading(false);
    }
  }, [token]);

  const fetchAnalytics = useCallback(async () => {
    const res = await axios.get(`${API}/admin/analytics`, headers);
    setAnalytics(res.data);
  }, [token]);

  const fetchTopStories = useCallback(async () => {
    const [storiesRes, pinnedRes] = await Promise.all([
      axios.get(`${API}/admin/top-stories`, headers),
      axios.get(`${API}/admin/pinned-articles`, headers),
    ]);
    setTopStories(storiesRes.data);
    setPinnedArticles(pinnedRes.data);
  }, [token]);

  const fetchPinnedArticles = useCallback(async () => {
    const res = await axios.get(`${API}/admin/pinned-articles`, headers);
    setPinnedArticles(res.data);
  }, [token]);

  const handlePinPriorityChange = (article) => (event) => {
    const nextPriority = Number(event.target.value);
    if (nextPriority === Number(article.pin_priority)) return;
    setPinPriorityDialog({
      open: true,
      articleId: article.article_id,
      title: article.title,
      currentPriority: Number(article.pin_priority),
      nextPriority,
    });
  };

  const confirmPinPriorityChange = async () => {
    const { articleId, nextPriority } = pinPriorityDialog;
    setPinPriorityDialog({ open: false, articleId: null, title: '', currentPriority: null, nextPriority: null });
    try {
      await axios.patch(`${API}/admin/articles/${articleId}/pin-priority`, { priority: nextPriority }, headers);
      await fetchPinnedArticles();
      toast.success(isHindi ? 'पिन क्रम अपडेट किया गया' : 'Pinned story order updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || (isHindi ? 'त्रुटि' : 'Error'));
    }
  };

  const fetchAuditLogs = useCallback(async () => {
    const res = await axios.get(`${API}/admin/audit-logs`, headers);
    setAuditLogs(res.data);
  }, [token]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        await fetchStats();
        if (activeTab === 'articles') await Promise.all([fetchArticles(), fetchPinnedArticles()]);
        else if (activeTab === 'categories') await fetchCategories();
        else if (activeTab === 'users') await fetchUsers();
        else if (activeTab === 'breaking') await fetchBreakingNews();
        else if (activeTab === 'analytics') await fetchAnalytics();
        else if (activeTab === 'audit') await fetchAuditLogs();
        else if (activeTab === 'top') await fetchTopStories();
      } catch (err) {
        toast.error(isHindi ? 'डेटा लोड करने में त्रुटि' : 'Error loading data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [activeTab, filter, userRoleFilter, articlePage, articleSearch]);

  // ── Article actions ──
  const articleAction = async (type, id, label) => {
    try {
      let pinResult = null;
      if (type === 'revoke')     await axios.put(`${API}/admin/articles/${id}/revoke`, {}, headers);
      if (type === 'republish')  await axios.put(`${API}/admin/articles/${id}/republish`, {}, headers);
      if (type === 'delete')     await axios.delete(`${API}/articles/${id}`, headers);
      if (type === 'pin') pinResult = await axios.put(`${API}/admin/articles/${id}/pin`, {}, headers);
      toast.success(type === 'pin'
        ? pinResult.data.pinned
          ? (isHindi ? `पिन किया गया · क्रम ${pinResult.data.pin_priority}` : `Pinned at position ${pinResult.data.pin_priority}`)
          : (isHindi ? 'पिन हटाया गया' : 'Unpinned')
        : (isHindi ? 'हो गया' : 'Done'));
      await fetchArticles();
      if (type === 'pin' || type === 'delete' || type === 'revoke') await fetchPinnedArticles();
      await fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || (isHindi ? 'त्रुटि' : 'Error'));
    } finally {
      setActionDialog({ open: false, type: null, articleId: null, label: '' });
    }
  };

  const handleBulkAction = async () => {
    if (!bulkAction || selectedIds.length === 0) return;
    try {
      await axios.post(`${API}/admin/articles/bulk`, { ids: selectedIds, action: bulkAction }, headers);
      toast.success(isHindi ? `${selectedIds.length} लेख अपडेट हुए` : `${selectedIds.length} articles updated`);
      setBulkAction('');
      await fetchArticles();
      await fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || (isHindi ? 'त्रुटि' : 'Error'));
    }
  };

  const handleExport = () => {
    window.open(`${API}/admin/articles/export?token=${token}`, '_blank');
    // fallback with auth header via fetch-blob
    fetch(`${API}/admin/articles/export`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob()).then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = 'articles.csv'; a.click();
        URL.revokeObjectURL(url);
      }).catch(() => toast.error('Export failed'));
  };

  const toggleSelectAll = () => {
    const visibleIds = filteredArticles.map(a => a.article_id);
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selectedIds.includes(id));
    setSelectedIds(allVisibleSelected
      ? selectedIds.filter(id => !visibleIds.includes(id))
      : [...new Set([...selectedIds, ...visibleIds])]);
  };

  const filteredArticles = articles;

  useEffect(() => {
    const lastPage = Math.max(1, Math.ceil(articleTotal / ARTICLES_PER_PAGE));
    if (articlePage > lastPage) setArticlePage(lastPage);
  }, [articlePage, articleTotal]);

  // ── Reporter actions ──
  const handleReporterAction = async () => {
    const { type, reporter } = reporterDialog;
    try {
      await axios.put(`${API}/admin/reporters/${reporter.user_id}/${type}`, {}, headers);
      toast.success(type === 'approve' ? (isHindi ? 'स्वीकृत' : 'Approved') : (isHindi ? 'अस्वीकृत' : 'Rejected'));
      await fetchUsers(); await fetchStats();
    } catch (err) { toast.error(isHindi ? 'त्रुटि' : 'Error'); }
    finally { setReporterDialog({ open: false, type: null, reporter: null }); }
  };

  // ── Category actions ──
  const handleSaveCategory = async () => {
    try {
      if (editingCategory) {
        await axios.put(`${API}/admin/categories/${editingCategory}`, { name: categoryForm.name, name_hi: categoryForm.name_hi, parent_id: categoryForm.parent_id || null }, headers);
        toast.success(isHindi ? 'अपडेट हुई' : 'Updated');
      } else {
        await axios.post(`${API}/admin/categories`, { ...categoryForm, parent_id: categoryForm.parent_id || null }, headers);
        toast.success(isHindi ? 'जोड़ी गई' : 'Added');
      }
      setCategoryForm({ id: '', name: '', name_hi: '', parent_id: '' }); setEditingCategory(null);
      await fetchCategories();
    } catch (err) { toast.error(err.response?.data?.detail || (isHindi ? 'त्रुटि' : 'Error')); }
  };

  const handleDeleteCategory = async () => {
    try {
      await axios.delete(`${API}/admin/categories/${categoryDialog.category.id}`, headers);
      toast.success(isHindi ? 'हटाई गई' : 'Deleted');
      await fetchCategories();
    } catch (err) { toast.error(err.response?.data?.detail || (isHindi ? 'त्रुटि' : 'Error')); }
    finally { setCategoryDialog({ open: false, category: null }); }
  };

  // ── User actions ──
  const handleUserAction = async (event) => {
    const { type, user } = userDialog;
    if ((type === 'create' || type === 'edit') && (
      !userForm.name.trim() || !userForm.email.trim() ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(userForm.email.trim()) ||
      !['admin', 'reporter', ...(type === 'edit' && userDialog.user?.role === 'reader' ? ['reader'] : [])].includes(userForm.role) ||
      (type === 'create' && (userForm.password.length < 8 || userForm.password.length > 72))
    )) {
      event?.preventDefault();
      toast.error(type === 'create' ? 'Enter a name, valid email, role, and password between 8 and 72 characters.' : 'Enter a name, valid email, and role.');
      return;
    }
    try {
      if (type === 'create') await axios.post(`${API}/admin/users`, userForm, headers);
      else if (type === 'edit') await axios.put(`${API}/admin/users/${user.user_id}`, userForm, headers);
      else if (type === 'activate') await axios.put(`${API}/admin/users/${user.user_id}/activate`, {}, headers);
      else if (type === 'deactivate') await axios.put(`${API}/admin/users/${user.user_id}/deactivate`, {}, headers);
      toast.success(type === 'create' ? 'User created' : type === 'edit' ? 'User updated' : 'Updated');
      await fetchUsers();
      if (type === 'create') await fetchStats();
    } catch (err) { toast.error(err.response?.data?.detail || (isHindi ? 'त्रुटि' : 'Error')); }
    finally { setUserDialog({ open: false, type: null, user: null }); }
  };

  const openUserEditor = (user) => {
    setUserForm({ name: user.name || '', email: user.email || '', phone: user.phone || '', role: user.role || 'reporter', password: '' });
    setShowTemporaryPassword(false);
    setUserDialog({ open: true, type: 'edit', user });
  };
  const openCreateUser = () => {
    setUserForm({ name: '', email: '', phone: '', role: 'reporter', password: '' });
    setShowTemporaryPassword(false);
    setUserDialog({ open: true, type: 'create', user: null });
  };

  // ── Breaking news actions ──
  const activeBreakingCount = breakingItems.filter((item) => item.is_active).length;
  const breakingArticleSearchTerm = breakingArticleSearch.trim().toLocaleLowerCase();
  const filteredBreakingArticleOptions = breakingArticleOptions.filter((article) => (
    article.article_id === breakingArticleId
    || !breakingArticleSearchTerm
    || [article.title, article.title_hi, article.article_id]
      .some((value) => String(value || '').toLocaleLowerCase().includes(breakingArticleSearchTerm))
  ));
  const editingBreakingArticleSearchTerm = editingBreakingArticleSearch.trim().toLocaleLowerCase();
  const filteredEditingBreakingArticleOptions = breakingArticleOptions.filter((article) => (
    article.article_id === editingBreakingArticleId
    || !editingBreakingArticleSearchTerm
    || [article.title, article.title_hi, article.article_id]
      .some((value) => String(value || '').toLocaleLowerCase().includes(editingBreakingArticleSearchTerm))
  ));
  const openBreakingForm = () => {
    if (activeBreakingCount >= 5) {
      setTickerLimitAlert(true);
      return;
    }
    setBreakingArticleSearch('');
    setShowBreakingForm(true);
    window.setTimeout(() => breakingFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 0);
  };

  const handleAddBreaking = async () => {
    if (!breakingText.trim()) return;
    if (activeBreakingCount >= 5) {
      setTickerLimitAlert(true);
      return;
    }
    try {
      await axios.post(`${API}/admin/breaking-news/items`, { text: breakingText, article_id: breakingArticleId || null }, headers);
      toast.success(isHindi ? 'ब्रेकिंग न्यूज़ जोड़ी गई' : 'Ticker item added');
      setBreakingText('');
      setBreakingArticleId('');
      setShowBreakingForm(false);
      await fetchBreakingNews();
    } catch (err) {
      if (err.response?.status === 409) setTickerLimitAlert(true);
      else toast.error(err.response?.data?.detail || (isHindi ? 'त्रुटि' : 'Error'));
    }
  };

  const handleUpdateBreaking = async (id) => {
    try {
      await axios.put(`${API}/admin/breaking-news/items/${id}`, {
        text: editingBreakingText,
        article_id: editingBreakingArticleId || null,
      }, headers);
      toast.success(isHindi ? 'टिकर अपडेट किया गया' : 'Ticker item updated');
      setEditingBreakingId(null);
      await fetchBreakingNews();
    } catch (err) { toast.error(err.response?.data?.detail || (isHindi ? 'त्रुटि' : 'Error')); }
  };

  const handleToggleBreaking = async (id, isActive) => {
    try {
      await axios.patch(`${API}/admin/breaking-news/items/${id}/status`, { is_active: isActive }, headers);
      await fetchBreakingNews();
    } catch (err) { toast.error(err.response?.data?.detail || (isHindi ? 'त्रुटि' : 'Error')); }
  };

  const handleDeleteBreaking = async (id) => {
    try {
      await axios.delete(`${API}/admin/breaking-news/items/${id}`, headers);
      toast.success(isHindi ? 'टिकर हटाया गया' : 'Ticker item deleted');
      await fetchBreakingNews();
    } catch (err) { toast.error(err.response?.data?.detail || (isHindi ? 'त्रुटि' : 'Error')); }
  };

  const confirmTickerAction = async () => {
    const { type, id, nextActive } = tickerActionDialog;
    setTickerActionDialog({ open: false, type: null, id: null, nextActive: null });
    if (type === 'delete') await handleDeleteBreaking(id);
    if (type === 'status') await handleToggleBreaking(id, nextActive);
  };

  // ── Helpers ──
  const fmtDate = (d) => { try { return format(new Date(d), 'MMM dd, yyyy HH:mm'); } catch { return d; } };
  const fmtDateShort = (d) => { try { return format(new Date(d), 'MMM dd, yyyy'); } catch { return d; } };

  const statusBadge = (status) => ({
    published: <span className="status-published flex items-center gap-1"><CheckCircle className="w-3 h-3" />{isHindi ? 'प्रकाशित' : 'Published'}</span>,
    draft:     <span className="status-draft">{isHindi ? 'ड्राफ्ट' : 'Draft'}</span>,
    revoked:   <span className="status-revoked flex items-center gap-1"><XCircle className="w-3 h-3" />{isHindi ? 'रद्द' : 'Revoked'}</span>,
  }[status] || <span className="status-draft">{status}</span>);

  const TABS = [
    { id: 'articles',  label: isHindi ? 'लेख' : 'Articles',       icon: FileText },
    { id: 'users',     label: isHindi ? 'उपयोगकर्ता' : 'Users',   icon: Shield },
    { id: 'categories',label: isHindi ? 'श्रेणियां' : 'Categories', icon: Tag },
    { id: 'breaking',  label: isHindi ? 'ब्रेकिंग' : 'Breaking',  icon: Radio },
    { id: 'analytics', label: isHindi ? 'विश्लेषण' : 'Analytics', icon: TrendingUp },
    { id: 'audit',     label: isHindi ? 'ऑडिट' : 'Audit Log',     icon: Activity },
  ];

  return (
    <div className="min-h-screen bg-[#ffffff]" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-4 py-8">

        {/* Header */}
        <div className="mb-8">
          <h1 className={`text-3xl font-bold text-[#121212] ${isHindi ? 'font-hindi-heading' : 'font-heading'}`}>
            {t('admin')} {isHindi ? 'डैशबोर्ड' : 'Dashboard'}
          </h1>
          <p className={`text-gray-600 mt-1 ${isHindi ? 'font-hindi' : ''}`}>
            {isHindi ? 'सभी लेख और उपयोगकर्ता प्रबंधित करें' : 'Manage all articles, users, and site settings'}
          </p>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6 md:mb-8">
            {[
              { label: isHindi ? 'कुल लेख' : 'Total', value: stats.total_articles, color: 'text-[#121212]', bg: 'bg-[#121212]/10', Icon: FileText },
              { label: t('published'), value: stats.published, color: 'text-green-600', bg: 'bg-green-100', Icon: CheckCircle },
              { label: t('drafts'), value: stats.drafts, color: 'text-amber-600', bg: 'bg-amber-100', Icon: BarChart3 },
              { label: isHindi ? 'रद्द' : 'Revoked', value: stats.revoked, color: 'text-red-600', bg: 'bg-red-100', Icon: AlertTriangle },
              { label: isHindi ? 'उपयोगकर्ता' : 'Users', value: stats.total_users, color: 'text-blue-600', bg: 'bg-blue-100', Icon: Users },
            ].map(({ label, value, color, bg, Icon }) => (
              <div key={label} className="stat-card !p-3 lg:!p-4">
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 ${bg} rounded flex items-center justify-center shrink-0`}>
                    <Icon className={`w-4 h-4 ${color}`} />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 leading-tight">{label}</p>
                    <p className={`text-xl font-bold ${color} leading-none mt-0.5`}>{value}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Tab bar */}
        <div className="flex gap-1 mb-6 border-b border-gray-200 overflow-x-auto">
          {TABS.map(({ id, label, icon: Icon, badge }) => (
            <button key={id} onClick={() => setActiveTab(id)}
              className={`px-4 py-2 text-sm font-semibold uppercase tracking-wider transition-colors whitespace-nowrap flex items-center gap-1.5 ${
                activeTab === id ? 'text-[#121212] border-b-2 border-[#121212]' : 'text-gray-500 hover:text-[#121212]'}`}
            >
              <Icon className="w-4 h-4" />{label}
              {badge > 0 && <span className="bg-amber-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full">{badge}</span>}
            </button>
          ))}
        </div>

        {/* ── ARTICLES TAB ── */}
        {activeTab === 'articles' && (
          <>
            <section className="bg-white border border-gray-200 p-4 mb-5">
              <div className="flex flex-wrap items-start justify-between gap-2 mb-4">
                <div>
                  <h3 className={`font-bold text-[#121212] ${isHindi ? 'font-hindi-heading' : 'font-heading'}`}>
                    {isHindi ? 'पिन की गई खबरों का क्रम' : 'Pinned story priority'}
                  </h3>
                  <p className={`text-sm text-gray-500 mt-1 ${isHindi ? 'font-hindi' : ''}`}>
                    {isHindi ? 'नई पिन की गई खबर सूची के अंत में जुड़ती है। क्रम बदलने पर खबरें अपने आप स्थान बदलती हैं।' : 'New pins are added at the end. Changing a number shifts the stories between positions automatically.'}
                  </p>
                </div>
                <span className="text-xs text-gray-500">{pinnedArticles.length} {isHindi ? 'पिन की गईं' : 'pinned'}</span>
              </div>
              {loading ? (
                <div className="space-y-2 animate-pulse" aria-label={isHindi ? 'पिन की गई खबरें लोड हो रही हैं' : 'Loading pinned stories'}>
                  {[0, 1, 2].map((item) => <div key={item} className="h-12 rounded-sm bg-gray-100" />)}
                </div>
              ) : pinnedArticles.length === 0 ? (
                <p className="py-3 text-sm text-gray-500">{isHindi ? 'अभी कोई खबर पिन नहीं है। लेख सूची में पिन बटन से खबर जोड़ें।' : 'No stories are pinned yet. Use the pin button in the articles list to add one.'}</p>
              ) : (
                <ol className="divide-y divide-gray-100">
                  {pinnedArticles.map((article) => (
                    <li key={article.article_id} className="flex flex-wrap items-center justify-between gap-3 py-3">
                      <div className="flex min-w-0 items-center gap-3">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#121212] text-sm font-bold text-white">{article.pin_priority}</span>
                        <Link to={`/article/${article.article_id}`} className={`min-w-0 line-clamp-2 text-sm font-semibold text-gray-900 hover:underline ${isHindi ? 'font-hindi' : ''}`}>
                          {isHindi && article.title_hi ? article.title_hi : article.title}
                        </Link>
                      </div>
                      <label className="flex shrink-0 items-center gap-2 text-xs text-gray-600">
                        <span>{isHindi ? 'स्थान' : 'Position'}</span>
                        <select value={article.pin_priority} onChange={handlePinPriorityChange(article)}
                          aria-label={`${isHindi ? 'पिन क्रम' : 'Pin position'}: ${article.title}`}
                          className="border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900">
                          {pinnedArticles.map((_, index) => <option key={index + 1} value={index + 1}>{index + 1}</option>)}
                        </select>
                      </label>
                    </li>
                  ))}
                </ol>
              )}
            </section>

            {/* Toolbar */}
            <div className="flex flex-wrap gap-2 mb-4 items-center justify-between">
              <div className="flex flex-1 gap-2 flex-wrap items-center">
                {['all', 'published', 'draft', 'revoked'].map(f => (
                  <button key={f} onClick={() => { setArticlePage(1); setFilter(f); }}
                    className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider rounded transition-colors ${
                      filter === f ? 'bg-[#121212] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                    {f === 'all' ? (isHindi ? 'सभी' : 'All') : f === 'revoked' ? (isHindi ? 'रद्द' : 'Revoked') : t(f === 'published' ? 'published' : 'drafts')}
                  </button>
                ))}
                <label className="relative w-full sm:w-72 sm:ml-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="search"
                    value={articleSearch}
                    onChange={event => { setArticlePage(1); setArticleSearch(event.target.value); }}
                    placeholder={isHindi ? 'लेख खोजें...' : 'Search articles...'}
                    aria-label={isHindi ? 'लेख खोजें' : 'Search articles'}
                    className="w-full border border-gray-300 rounded pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-[#121212]"
                  />
                </label>
              </div>
              <Button variant="outline" size="sm" onClick={handleExport} className="text-xs border-[#121212] text-[#121212]">
                <Download className="w-3.5 h-3.5 mr-1" />{isHindi ? 'CSV डाउनलोड' : 'Export CSV'}
              </Button>
            </div>

            {/* Bulk actions bar */}
            {selectedIds.length > 0 && (
              <div className="flex items-center gap-3 mb-3 p-3 bg-[#121212]/5 border border-[#121212]/20 rounded">
                <span className="text-sm font-semibold text-[#121212]">{selectedIds.length} {isHindi ? 'चुने गए' : 'selected'}</span>
                <select value={bulkAction} onChange={e => setBulkAction(e.target.value)}
                  className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:border-[#121212]">
                  <option value="">{isHindi ? 'कार्रवाई चुनें' : 'Choose action'}</option>
                  <option value="revoke">{isHindi ? 'रद्द करें' : 'Revoke'}</option>
                  <option value="republish">{isHindi ? 'पुनः प्रकाशित' : 'Republish'}</option>
                  <option value="delete">{isHindi ? 'हटाएं' : 'Delete'}</option>
                </select>
                <Button size="sm" disabled={!bulkAction} onClick={handleBulkAction}
                  className="bg-[#121212] hover:bg-[#000000] text-white text-xs">
                  {isHindi ? 'लागू करें' : 'Apply'}
                </Button>
                <button className="text-xs text-gray-500 hover:text-gray-700" onClick={() => setSelectedIds([])}>
                  {isHindi ? 'रद्द करें' : 'Cancel'}
                </button>
              </div>
            )}

            <div className="bg-white border border-gray-200 overflow-hidden">
              {loading ? (
                <div className="p-8 text-center"><div className="animate-pulse"><div className="h-4 bg-gray-200 rounded w-1/4 mx-auto mb-4" /><div className="h-4 bg-gray-200 rounded w-1/2 mx-auto" /></div></div>
              ) : filteredArticles.length === 0 ? (
                <div className="p-8 text-center"><FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" /><p className="text-gray-500">{isHindi ? 'कोई लेख नहीं' : 'No articles found'}</p></div>
              ) : (
                <>
                  <div className="hidden md:block overflow-x-auto">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th><input type="checkbox" checked={filteredArticles.length > 0 && filteredArticles.every(article => selectedIds.includes(article.article_id))} onChange={toggleSelectAll} className="rounded" /></th>
                          <th className="w-14">{isHindi ? 'क्र.सं.' : 'S.No.'}</th>
                          <th className={isHindi ? 'font-hindi' : ''}>{t('title')}</th>
                          <th>{isHindi ? 'पूर्वावलोकन' : 'Preview'}</th>
                          <th className={isHindi ? 'font-hindi' : ''}>{t('author')}</th>
                          <th className={isHindi ? 'font-hindi' : ''}>{t('category')}</th>
                          <th className={isHindi ? 'font-hindi' : ''}>{t('status')}</th>
                          <th className={isHindi ? 'font-hindi' : ''}>{t('views')}</th>
                          <th className={isHindi ? 'font-hindi' : ''}>{isHindi ? 'तारीख' : 'Date'}</th>
                          <th className={isHindi ? 'font-hindi' : ''}>{t('actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredArticles.map((article, index) => (
                          <tr key={article.article_id} data-testid={`admin-article-row-${article.article_id}`}
                            className={selectedIds.includes(article.article_id) ? 'bg-[#121212]/5' : ''}>
                            <td>
                              <input type="checkbox" checked={selectedIds.includes(article.article_id)}
                                onChange={e => setSelectedIds(e.target.checked ? [...selectedIds, article.article_id] : selectedIds.filter(i => i !== article.article_id))}
                                className="rounded" />
                            </td>
                            <td className="text-sm text-gray-500">{(articlePage - 1) * ARTICLES_PER_PAGE + index + 1}</td>
                            <td>
                              <div className="max-w-xs flex items-center gap-1.5">
                                {article.pinned && <span className="inline-flex items-center gap-0.5 shrink-0" title={`Pinned at position ${article.pin_priority}`}><Pin className="w-3 h-3 text-[#121212]" /><span className="text-[10px] font-bold text-gray-500">{article.pin_priority}</span></span>}
                                <Link to={`/article/${article.article_id}`} className={`font-semibold text-gray-900 line-clamp-1 hover:text-[#121212] ${isHindi ? 'font-hindi' : ''}`}>
                                  {isHindi && article.title_hi ? article.title_hi : article.title}
                                </Link>
                              </div>
                            </td>
                            <td>
                              {article.image_url ? <img src={article.image_url} alt={article.title || ''} className="w-16 h-12 object-cover rounded" loading="lazy" /> : <span className="text-xs text-gray-400">—</span>}
                            </td>
                            <td className="text-sm text-gray-600">{article.author_name}</td>
                            <td><span className={`category-pill ${isHindi ? 'font-hindi' : ''}`}>{t(article.category.toLowerCase())}</span></td>
                            <td>{statusBadge(article.status)}</td>
                            <td>{article.views || 0}</td>
                            <td className="text-sm text-gray-500 whitespace-nowrap">{fmtDateShort(article.created_at)}</td>
                            <td>
                              <div className="flex items-center gap-1">
                                <Link to={`/article/${article.article_id}`}>
                                  <Button variant="ghost" size="sm" className="text-gray-500 hover:text-[#121212] p-1.5" title="View"><Eye className="w-3.5 h-3.5" /></Button>
                                </Link>
                                <Button variant="ghost" size="sm"
                                  className={`p-1.5 ${article.pinned ? 'text-[#121212]' : 'text-gray-400 hover:text-[#121212]'}`}
                                  title={article.pinned ? 'Unpin' : 'Pin: force into Top Stories (override)'}
                                  onClick={() => setActionDialog({ open: true, type: 'pin', articleId: article.article_id, label: article.title })}>
                                  <Pin className="w-3.5 h-3.5" />
                                </Button>
                                {article.status === 'published' && (
                                  <Button variant="ghost" size="sm" className="text-amber-500 hover:text-amber-600 p-1.5" title="Revoke"
                                    onClick={() => setActionDialog({ open: true, type: 'revoke', articleId: article.article_id, label: article.title })}>
                                    <XCircle className="w-3.5 h-3.5" />
                                  </Button>
                                )}
                                {article.status === 'revoked' && (
                                  <Button variant="ghost" size="sm" className="text-green-600 hover:text-green-700 p-1.5" title="Republish"
                                    onClick={() => setActionDialog({ open: true, type: 'republish', articleId: article.article_id, label: article.title })}>
                                    <RefreshCw className="w-3.5 h-3.5" />
                                  </Button>
                                )}
                                <Button variant="ghost" size="sm" className="text-gray-400 hover:text-red-600 p-1.5" title="Delete"
                                  onClick={() => setActionDialog({ open: true, type: 'delete', articleId: article.article_id, label: article.title })}>
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Card View */}
                  <div className="md:hidden divide-y divide-gray-100">
                    <div className="p-3 bg-gray-50 flex items-center justify-between border-b border-gray-200">
                      <label className="flex items-center gap-2 text-sm text-gray-600 font-medium cursor-pointer">
                        <input type="checkbox" checked={filteredArticles.length > 0 && filteredArticles.every(article => selectedIds.includes(article.article_id))} onChange={toggleSelectAll} className="rounded" />
                        {isHindi ? 'सभी चुनें' : 'Select All'}
                      </label>
                    </div>
                    {filteredArticles.map((article, index) => (
                      <div key={article.article_id} className={`p-4 flex flex-col gap-3 ${selectedIds.includes(article.article_id) ? 'bg-[#121212]/5' : 'bg-white'}`}>
                        <div className="flex gap-3 items-start relative">
                          <input type="checkbox" checked={selectedIds.includes(article.article_id)}
                                 onChange={e => setSelectedIds(e.target.checked ? [...selectedIds, article.article_id] : selectedIds.filter(i => i !== article.article_id))}
                                 className="rounded mt-1 shrink-0 cursor-pointer" />
                          <span className="text-xs text-gray-500 shrink-0 mt-1">{isHindi ? 'क्र.सं.' : 'S.No.'}: {(articlePage - 1) * ARTICLES_PER_PAGE + index + 1}</span>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5 mb-1.5">
                              {article.pinned && <span className="inline-flex items-center gap-0.5 shrink-0"><Pin className="w-3 h-3 text-[#121212]" /><span className="text-[10px] font-bold text-gray-500">{article.pin_priority}</span></span>}
                              <span className={`category-pill text-[10px] py-0.5 px-2 bg-gray-100 ${isHindi ? 'font-hindi' : ''}`}>{t(article.category.toLowerCase())}</span>
                            </div>
                            <Link to={`/article/${article.article_id}`} className={`font-semibold text-gray-900 line-clamp-2 leading-snug ${isHindi ? 'font-hindi' : ''}`}>
                              {isHindi && article.title_hi ? article.title_hi : article.title}
                            </Link>
                          </div>
                        </div>
                        <div className="pl-7">
                          <p className="text-xs text-gray-500 mb-1">{isHindi ? 'पूर्वावलोकन' : 'Preview'}</p>
                          {article.image_url ? <img src={article.image_url} alt={article.title || ''} className="w-20 h-14 object-cover rounded" loading="lazy" /> : <span className="text-xs text-gray-400">—</span>}
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-gray-500 pl-7">
                          <span className="font-medium text-gray-700 bg-gray-100 px-2 py-0.5 rounded text-[10px]">{article.author_name}</span>
                          <div className="shrink-0">{statusBadge(article.status)}</div>
                          <span className="flex items-center gap-1"><Eye className="w-3.5 h-3.5" />{article.views || 0}</span>
                          <span>{fmtDateShort(article.created_at)}</span>
                        </div>

                        <div className="flex items-center gap-1 mt-2 pt-3 border-t border-gray-100 pl-7 overflow-x-auto pb-1">
                          <Link to={`/article/${article.article_id}`} className="shrink-0">
                            <Button variant="ghost" size="sm" className="text-gray-500 hover:text-[#121212] h-8 px-2 bg-gray-50"><Eye className="w-4 h-4" /></Button>
                          </Link>
                          <Button variant="ghost" size="sm"
                            className={`h-8 px-2 shrink-0 bg-gray-50 ${article.pinned ? 'text-[#121212]' : 'text-gray-400 hover:text-[#121212]'}`}
                            onClick={() => setActionDialog({ open: true, type: 'pin', articleId: article.article_id, label: article.title })}>
                            <Pin className="w-4 h-4" />
                          </Button>
                          {article.status === 'published' && (
                            <Button variant="ghost" size="sm" className="text-amber-500 hover:text-amber-600 h-8 px-2 shrink-0 bg-gray-50"
                              onClick={() => setActionDialog({ open: true, type: 'revoke', articleId: article.article_id, label: article.title })}>
                              <XCircle className="w-4 h-4" />
                            </Button>
                          )}
                          {article.status === 'revoked' && (
                            <Button variant="ghost" size="sm" className="text-green-600 hover:text-green-700 h-8 px-2 shrink-0 bg-gray-50"
                              onClick={() => setActionDialog({ open: true, type: 'republish', articleId: article.article_id, label: article.title })}>
                              <RefreshCw className="w-4 h-4" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" className="text-gray-400 hover:text-red-600 h-8 px-2 ml-auto shrink-0 bg-red-50"
                            onClick={() => setActionDialog({ open: true, type: 'delete', articleId: article.article_id, label: article.title })}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 py-4 text-sm text-gray-600">
              <span>
                {articleTotal === 0
                  ? (isHindi ? 'कोई लेख नहीं' : 'No articles')
                  : `${(articlePage - 1) * ARTICLES_PER_PAGE + 1}–${Math.min(articlePage * ARTICLES_PER_PAGE, articleTotal)} ${isHindi ? 'में से' : 'of'} ${articleTotal}`}
              </span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" className="min-w-9 px-2" aria-label="First page" title="First page"
                  disabled={articlePage <= 1 || loading} onClick={() => setArticlePage(1)}>&lt;&lt;</Button>
                <Button variant="outline" size="sm" className="min-w-9 px-2" aria-label="Previous page" title="Previous page"
                  disabled={articlePage <= 1 || loading} onClick={() => setArticlePage(page => Math.max(1, page - 1))}>&lt;</Button>
                <span>Page {articlePage} / {Math.max(1, Math.ceil(articleTotal / ARTICLES_PER_PAGE))}</span>
                <Button variant="outline" size="sm" className="min-w-9 px-2" aria-label="Next page" title="Next page"
                  disabled={articlePage >= Math.ceil(articleTotal / ARTICLES_PER_PAGE) || loading} onClick={() => setArticlePage(page => page + 1)}>&gt;</Button>
                <Button variant="outline" size="sm" className="min-w-9 px-2" aria-label="Last page" title="Last page"
                  disabled={articlePage >= Math.ceil(articleTotal / ARTICLES_PER_PAGE) || loading}
                  onClick={() => setArticlePage(Math.max(1, Math.ceil(articleTotal / ARTICLES_PER_PAGE)))}>&gt;&gt;</Button>
              </div>
            </div>
          </>
        )}

        {/* ── REPORTERS TAB ── */}
        {activeTab === 'users' && (
          <>
            <div className="flex justify-end mb-4">
              <Button onClick={openCreateUser} className="bg-[#121212] hover:bg-black text-white">
                <Plus className="w-4 h-4 mr-2" />{isHindi ? 'नया उपयोगकर्ता' : 'Add User'}
              </Button>
            </div>
            <div className="flex gap-2 mb-4">
              {['all', 'admin', 'reporter'].map(f => (
                <button key={f} onClick={() => setUserRoleFilter(f)}
                  className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider rounded transition-colors ${
                    userRoleFilter === f ? 'bg-[#121212] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                  {f === 'all' ? (isHindi ? 'सभी' : 'All') : f === 'admin' ? 'Admin' : (isHindi ? 'रिपोर्टर' : 'Reporter')}
                </button>
              ))}
            </div>
            <div className="bg-white border border-gray-200 overflow-x-auto">
              {loading ? <div className="p-8 animate-pulse"><div className="h-4 bg-gray-200 rounded w-1/2 mx-auto" /></div>
              : users.length === 0 ? <div className="p-8 text-center"><Users className="w-12 h-12 text-gray-300 mx-auto mb-4" /><p className="text-gray-500">{isHindi ? 'कोई उपयोगकर्ता नहीं' : 'No users found'}</p></div>
              : (
                <>
                  <div className="hidden md:block overflow-x-auto">
                    <table className="data-table">
                      <thead><tr>
                        <th>{isHindi ? 'नाम' : 'Name'}</th>
                        <th>{isHindi ? 'ईमेल / मोबाइल' : 'Email / Mobile'}</th>
                        <th>{isHindi ? 'भूमिका' : 'Role'}</th>
                        <th>{isHindi ? 'स्थिति' : 'Status'}</th>
                        <th>{isHindi ? 'सक्रिय' : 'Active'}</th>
                        <th>{isHindi ? 'तारीख' : 'Joined'}</th>
                        <th>{t('actions')}</th>
                      </tr></thead>
                      <tbody>
                        {users.map(u => (
                          <tr key={u.user_id}>
                            <td className="font-semibold text-gray-900">{u.name}</td>
                            <td className="text-sm text-gray-600">{u.email}{u.phone && <span className="block text-xs text-gray-500">+91 {u.phone}</span>}</td>
                            <td>
                              <span className={`text-xs font-bold px-2 py-1 rounded ${u.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                                {u.role}
                              </span>
                            </td>
                            <td>
                              {u.status === 'approved' && <span className="text-xs font-semibold text-green-600">{isHindi ? 'स्वीकृत' : 'Approved'}</span>}
                              {u.status === 'pending'  && <span className="text-xs font-semibold text-amber-600">{isHindi ? 'लंबित' : 'Pending'}</span>}
                              {u.status === 'rejected' && <span className="text-xs font-semibold text-red-600">{isHindi ? 'अस्वीकृत' : 'Rejected'}</span>}
                            </td>
                            <td>
                              {u.is_active
                                ? <span className="text-xs font-semibold text-green-600">{isHindi ? 'हाँ' : 'Yes'}</span>
                                : <span className="text-xs font-semibold text-red-600">{isHindi ? 'नहीं' : 'No'}</span>}
                            </td>
                            <td className="text-sm text-gray-500 whitespace-nowrap">{fmtDateShort(u.created_at)}</td>
                            <td>
                              <div className="flex gap-1">
                                <Button variant="ghost" size="sm" className="text-blue-500 hover:bg-blue-50 p-1.5" title="Edit user"
                                  onClick={() => openUserEditor(u)}>
                                  <Pencil className="w-3.5 h-3.5" />
                                </Button>
                                {u.is_active
                                  ? <Button variant="ghost" size="sm" className="text-red-500 hover:bg-red-50 p-1.5" title="Deactivate"
                                      onClick={() => setUserDialog({ open: true, type: 'deactivate', user: u })}>
                                      <XCircle className="w-3.5 h-3.5" />
                                    </Button>
                                  : <Button variant="ghost" size="sm" className="text-green-600 hover:bg-green-50 p-1.5" title="Activate"
                                      onClick={() => setUserDialog({ open: true, type: 'activate', user: u })}>
                                      <CheckCircle className="w-3.5 h-3.5" />
                                    </Button>
                                }
                                {u.role === 'reporter' && u.status !== 'approved' && <Button variant="ghost" size="sm" className="text-green-600 hover:bg-green-50 p-1.5" title="Approve reporter" onClick={() => setReporterDialog({ open: true, type: 'approve', reporter: u })}><UserCheck className="w-3.5 h-3.5" /></Button>}
                                {u.role === 'reporter' && u.status !== 'rejected' && <Button variant="ghost" size="sm" className="text-red-500 hover:bg-red-50 p-1.5" title="Reject reporter" onClick={() => setReporterDialog({ open: true, type: 'reject', reporter: u })}><UserX className="w-3.5 h-3.5" /></Button>}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Card View for Users */}
                  <div className="md:hidden divide-y divide-gray-100">
                    {users.map(u => (
                      <div key={u.user_id} className="p-4 bg-white flex flex-col gap-2">
                        <div className="flex justify-between items-start gap-4">
                          <div>
                            <p className="font-semibold text-gray-900 leading-tight">{u.name}</p>
                            <p className="text-xs text-gray-500 mt-0.5">{u.email}</p>
                            {u.phone && <p className="text-xs text-gray-500">+91 {u.phone}</p>}
                          </div>
                          <div className="shrink-0 flex flex-col gap-1 items-end">
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${u.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                              {u.role.toUpperCase()}
                            </span>
                            {u.is_active
                              ? <span className="text-[10px] font-semibold text-green-600 bg-green-50 px-1.5 py-0.5 rounded">{isHindi ? 'सक्रिय' : 'Active'}</span>
                              : <span className="text-[10px] font-semibold text-red-600 bg-red-50 px-1.5 py-0.5 rounded">{isHindi ? 'निष्क्रिय' : 'Inactive'}</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-[11px] text-gray-500 mt-1">
                          <span>
                            {u.status === 'approved' && <span className="font-semibold text-green-600">{isHindi ? 'स्वीकृत' : 'Approved'}</span>}
                            {u.status === 'pending'  && <span className="font-semibold text-amber-600">{isHindi ? 'लंबित' : 'Pending'}</span>}
                            {u.status === 'rejected' && <span className="font-semibold text-red-600">{isHindi ? 'अस्वीकृत' : 'Rejected'}</span>}
                          </span>
                          <span>•</span>
                          <span>{fmtDateShort(u.created_at)}</span>
                        </div>
                        <div className="flex justify-end gap-2 mt-2 pt-3 border-t border-gray-50">
                          <Button variant="outline" size="sm" className="text-blue-500 border-blue-200 hover:bg-blue-50 h-8 px-2"
                            onClick={() => openUserEditor(u)}>
                            <Pencil className="w-3 h-3 mr-1" /> Edit
                          </Button>
                          {u.is_active
                            ? <Button variant="outline" size="sm" className="text-red-500 border-red-200 hover:bg-red-50 h-8 px-2"
                                onClick={() => setUserDialog({ open: true, type: 'deactivate', user: u })}>
                                <XCircle className="w-3 h-3 mr-1" /> Deactivate
                              </Button>
                            : <Button variant="outline" size="sm" className="text-green-600 border-green-200 hover:bg-green-50 h-8 px-2"
                                onClick={() => setUserDialog({ open: true, type: 'activate', user: u })}>
                                <CheckCircle className="w-3 h-3 mr-1" /> Activate
                              </Button>
                          }
                          {u.role === 'reporter' && u.status !== 'approved' && <Button variant="outline" size="sm" className="text-green-600 border-green-200 hover:bg-green-50 h-8 px-2" onClick={() => setReporterDialog({ open: true, type: 'approve', reporter: u })}><UserCheck className="w-3 h-3 mr-1" />Approve</Button>}
                          {u.role === 'reporter' && u.status !== 'rejected' && <Button variant="outline" size="sm" className="text-red-500 border-red-200 hover:bg-red-50 h-8 px-2" onClick={() => setReporterDialog({ open: true, type: 'reject', reporter: u })}><UserX className="w-3 h-3 mr-1" />Reject</Button>}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </>
        )}

        {/* ── CATEGORIES TAB ── */}
        {activeTab === 'categories' && (
          <div className="space-y-6">
            <div ref={categoryFormRef} className="bg-white border border-gray-200 p-6 scroll-mt-24">
              <h2 className={`text-lg font-bold text-[#121212] mb-4 flex items-center gap-2 ${isHindi ? 'font-hindi-heading' : 'font-heading'}`}>
                <Tag className="w-5 h-5" />
                {editingCategory ? (isHindi ? 'श्रेणी संपादित करें' : 'Edit Category') : (isHindi ? 'नई श्रेणी जोड़ें' : 'Add New Category')}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {!editingCategory && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">ID <span className="text-red-500">*</span> <span className="text-xs font-normal text-gray-400">(e.g. sports)</span></label>
                    <input type="text" value={categoryForm.id}
                      onChange={e => setCategoryForm(p => ({ ...p, id: e.target.value.toLowerCase().replace(/\s+/g, '_') }))}
                      placeholder="category_id" className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#121212]" />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">{isHindi ? 'नाम (अंग्रेजी)' : 'Name (English)'} <span className="text-red-500">*</span></label>
                  <input type="text" value={categoryForm.name} onChange={e => setCategoryForm(p => ({ ...p, name: e.target.value }))}
                    placeholder="Sports" className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#121212]" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1 font-hindi">{isHindi ? 'नाम (हिंदी)' : 'Name (Hindi)'} <span className="text-red-500">*</span></label>
                  <input type="text" value={categoryForm.name_hi} onChange={e => setCategoryForm(p => ({ ...p, name_hi: e.target.value }))}
                    placeholder="खेल" className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm font-hindi focus:outline-none focus:border-[#121212]" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">{isHindi ? 'मुख्य श्रेणी (वैकल्पिक)' : 'Parent (optional)'}</label>
                  <select value={categoryForm.parent_id || ''} onChange={e => setCategoryForm(p => ({ ...p, parent_id: e.target.value }))}
                    className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#121212]">
                    <option value="">{isHindi ? '— मुख्य श्रेणी —' : '— Top-level category —'}</option>
                    {categories.filter(c => !c.parent_id && c.id !== editingCategory).map(c => (
                      <option key={c.id} value={c.id}>{isHindi ? c.name_hi : c.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex gap-3 mt-4">
                <Button onClick={handleSaveCategory} disabled={!categoryForm.name || !categoryForm.name_hi || (!editingCategory && !categoryForm.id)}
                  className="bg-[#121212] hover:bg-[#000000] text-white">
                  <Plus className="w-4 h-4 mr-2" />{editingCategory ? (isHindi ? 'अपडेट करें' : 'Update') : (isHindi ? 'जोड़ें' : 'Add')}
                </Button>
                {editingCategory && (
                  <Button variant="outline" onClick={() => { setEditingCategory(null); setCategoryForm({ id: '', name: '', name_hi: '', parent_id: '' }); }}>
                    {isHindi ? 'रद्द करें' : 'Cancel'}
                  </Button>
                )}
              </div>
            </div>
            <div className="bg-white border border-gray-200 overflow-x-auto">
              {categories.length === 0 ? (
                <div className="p-8 text-center"><Tag className="w-12 h-12 text-gray-300 mx-auto mb-4" /><p className="text-gray-500">{isHindi ? 'कोई श्रेणी नहीं' : 'No categories'}</p></div>
              ) : (
                <table className="data-table">
                  <thead><tr><th>ID</th><th>{isHindi ? 'नाम (अंग्रेजी)' : 'Name (English)'}</th><th>{isHindi ? 'नाम (हिंदी)' : 'Name (Hindi)'}</th><th>{isHindi ? 'मुख्य श्रेणी' : 'Parent'}</th><th>{t('actions')}</th></tr></thead>
                  <tbody>
                    {categories.map(cat => (
                      <tr key={cat.id}>
                        <td><code className="text-xs bg-gray-100 px-2 py-1 rounded">{cat.id}</code></td>
                        <td className="font-semibold">{cat.name}</td>
                        <td className="font-hindi">{cat.name_hi}</td>
                        <td className="text-sm text-gray-500">{cat.parent_id ? (categories.find(c => c.id === cat.parent_id)?.[isHindi ? 'name_hi' : 'name'] || cat.parent_id) : '—'}</td>
                        <td>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" className="text-blue-500 hover:bg-blue-50 p-1.5"
                              onClick={() => {
                                setEditingCategory(cat.id);
                                setCategoryForm({ id: cat.id, name: cat.name, name_hi: cat.name_hi, parent_id: cat.parent_id || '' });
                                categoryFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                              }}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" className="text-gray-400 hover:text-red-600 hover:bg-red-50 p-1.5"
                              onClick={() => setCategoryDialog({ open: true, category: cat })}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* ── BREAKING NEWS TAB ── */}
        {activeTab === 'breaking' && (
          <div className="space-y-6">
            <div className="bg-white border border-gray-200 p-6">
              <div className="mb-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className={`text-lg font-bold text-[#121212] flex items-center gap-2 ${isHindi ? 'font-hindi-heading' : 'font-heading'}`}>
                  <Radio className="w-5 h-5 text-red-600" />{isHindi ? 'ब्रेकिंग न्यूज़ टिकर' : 'Breaking News Ticker'}
                </h2>
                <Button onClick={openBreakingForm} className="bg-red-600 hover:bg-red-700 text-white">
                  <Plus className="w-4 h-4 mr-2" />{isHindi ? 'टिकर जोड़ें' : 'Add ticker'}
                </Button>
                </div>
                <p className={`text-sm text-gray-500 mt-2 ${isHindi ? 'font-hindi' : ''}`}>
                  {isHindi
                    ? 'अधिकतम 5 सक्रिय टिकर दिखते हैं। टॉगल बंद करके जगह खाली करें; निष्क्रिय टिकर संपादित या हटाए जा सकते हैं।'
                    : 'Up to 5 active tickers appear on the site. Deactivate one to free a slot; inactive tickers can still be edited or deleted.'}
                </p>
              </div>

              <div className="space-y-3">
                {breakingItemsLoading && breakingItems.length === 0
                  ? [0, 1, 2].map((skeleton) => (
                    <div key={`ticker-skeleton-${skeleton}`} className="border border-gray-200 rounded-sm p-4 animate-pulse" role="status" aria-label={isHindi ? 'टिकर लोड हो रहे हैं' : 'Loading tickers'}>
                      <div className="flex items-center justify-between gap-3 mb-4">
                        <div className="h-3 w-20 rounded bg-gray-200" />
                        <div className="h-5 w-12 rounded-full bg-gray-200" />
                      </div>
                      <div className="h-4 w-full rounded bg-gray-200 mb-2" />
                      <div className="h-4 w-2/3 rounded bg-gray-200" />
                    </div>
                  ))
                  : breakingItems.map((item, index) => (
                  <div key={item.id} className={`border rounded-sm p-4 ${item.is_active ? 'border-gray-300' : 'border-gray-200 bg-gray-50'}`}>
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-bold text-gray-500">#{index + 1}</span>
                        <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded ${item.is_active ? 'bg-green-50 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                          {item.is_active ? (isHindi ? 'सक्रिय' : 'Active') : (isHindi ? 'निष्क्रिय' : 'Inactive')}
                        </span>
                        <span className="text-xs text-gray-400">{fmtDate(item.updated_at)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">{isHindi ? 'दिखाएँ' : 'Show'}</span>
                        <Switch checked={Boolean(item.is_active)} onCheckedChange={(checked) => setTickerActionDialog({ open: true, type: 'status', id: item.id, nextActive: checked })} aria-label={isHindi ? 'टिकर स्थिति बदलें' : 'Toggle ticker status'} />
                      </div>
                    </div>
                    {
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm text-gray-900 whitespace-pre-wrap ${isHindi ? 'font-hindi' : ''}`}>{item.text}</p>
                          {item.article_id && (
                            <Link to={`/article/${item.article_id}`} className="inline-block mt-2 text-xs text-blue-700 hover:underline">
                              {isHindi ? 'संलग्न लेख: ' : 'Attached article: '}{(isHindi && item.article_title_hi) || item.article_title || item.article_id}
                            </Link>
                          )}
                        </div>
                        <div className="flex shrink-0 gap-1">
                          <Button variant="ghost" size="sm" onClick={() => {
                            setEditingBreakingId(item.id);
                            setEditingBreakingText(item.text);
                            setEditingBreakingArticleId(item.article_id || '');
                            setEditingBreakingArticleSearch('');
                            setEditingBreakingArticlePickerOpen(false);
                          }} aria-label={isHindi ? 'संपादित करें' : 'Edit ticker'}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setTickerActionDialog({ open: true, type: 'delete', id: item.id, nextActive: null })} aria-label={isHindi ? 'हटाएँ' : 'Delete ticker'} className="text-red-600">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    }
                  </div>
                ))}
                {!breakingItemsLoading && breakingItems.length === 0 && (
                  <p className="py-6 text-center text-sm text-gray-500">{isHindi ? 'अभी कोई टिकर नहीं है।' : 'No tickers have been added yet.'}</p>
                )}
              </div>
            </div>

            {showBreakingForm && <div ref={breakingFormRef} className="bg-white border border-gray-200 p-6">
              <h3 className={`text-base font-bold text-[#121212] mb-2 ${isHindi ? 'font-hindi-heading' : 'font-heading'}`}>
                {isHindi ? 'टिकर जोड़ें' : 'Add a ticker'}
              </h3>
              <p className={`text-sm text-gray-500 mb-4 ${isHindi ? 'font-hindi' : ''}`}>
                {isHindi ? `${activeBreakingCount}/5 टिकर सक्रिय` : `${activeBreakingCount}/5 active ticker slots`}
              </p>
              <textarea value={breakingText} onChange={(e) => setBreakingText(e.target.value)} rows={3} maxLength={2000}
                placeholder={isHindi ? 'एक ब्रेकिंग न्यूज़ शीर्षक लिखें...' : 'Enter a ticker headline...'}
                className={`w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#121212] mb-3 ${isHindi ? 'font-hindi' : ''}`} />
              <div className="relative mb-4">
                <button
                  type="button"
                  onClick={() => {
                    setBreakingArticlePickerOpen((open) => !open);
                    setBreakingArticleSearch('');
                  }}
                  aria-haspopup="listbox"
                  aria-expanded={breakingArticlePickerOpen}
                  className="w-full flex items-center justify-between gap-3 border border-gray-300 rounded-sm px-3 py-2 text-left text-sm bg-white focus:outline-none focus:border-[#121212]"
                >
                  <span className="truncate">
                    {breakingArticleId
                      ? (() => {
                        const selectedArticle = breakingArticleOptions.find((article) => article.article_id === breakingArticleId);
                        return selectedArticle
                          ? ((isHindi && selectedArticle.title_hi) || selectedArticle.title)
                          : breakingArticleId;
                      })()
                      : (isHindi ? 'कोई लेख संलग्न न करें' : 'No article attached')}
                  </span>
                  <ChevronDown className="w-4 h-4 shrink-0 text-gray-500" />
                </button>
                {breakingArticlePickerOpen && (
                  <div className="absolute z-40 mt-1 w-full border border-gray-300 bg-white shadow-lg">
                    <label className="relative block p-2 border-b border-gray-200">
                      <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="search"
                        autoFocus
                        value={breakingArticleSearch}
                        onChange={(event) => setBreakingArticleSearch(event.target.value)}
                        placeholder={isHindi ? 'खबर खोजें...' : 'Search articles...'}
                        aria-label={isHindi ? 'खबर खोजें' : 'Search articles'}
                        className="w-full border border-gray-300 rounded-sm pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-[#121212]"
                      />
                    </label>
                    <div role="listbox" className="max-h-56 overflow-y-auto py-1">
                      <button
                        type="button"
                        role="option"
                        aria-selected={!breakingArticleId}
                        onClick={() => {
                          setBreakingArticleId('');
                          setBreakingArticlePickerOpen(false);
                          setBreakingArticleSearch('');
                        }}
                        className="w-full px-3 py-2 text-left text-sm text-gray-600 hover:bg-gray-100"
                      >
                        {isHindi ? 'कोई लेख संलग्न न करें' : 'No article attached'}
                      </button>
                      {filteredBreakingArticleOptions.map((article) => (
                        <button
                          key={article.article_id}
                          type="button"
                          role="option"
                          aria-selected={breakingArticleId === article.article_id}
                          onClick={() => {
                            setBreakingArticleId(article.article_id);
                            setBreakingArticlePickerOpen(false);
                            setBreakingArticleSearch('');
                          }}
                          className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 ${isHindi ? 'font-hindi' : ''}`}
                        >
                          {(isHindi && article.title_hi) || article.title}
                        </button>
                      ))}
                      {filteredBreakingArticleOptions.length === 0 && (
                        <p className="px-3 py-3 text-sm text-gray-500">
                          {isHindi ? 'कोई खबर नहीं मिली' : 'No articles found'}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <Button onClick={handleAddBreaking}
                disabled={!breakingText.trim()}
                className="bg-red-600 hover:bg-red-700 text-white">
                <Plus className="w-4 h-4 mr-2" />{isHindi ? 'टिकर जोड़ें' : 'Add ticker'}
              </Button>
              <Button variant="outline" onClick={() => setShowBreakingForm(false)} className="ml-2">
                {isHindi ? 'रद्द करें' : 'Cancel'}
              </Button>
            </div>}
          </div>
        )}
        {/* ── ANALYTICS TAB ── */}
        {activeTab === 'analytics' && (
          <div className="space-y-6">
            {loading ? <div className="p-8 text-center animate-pulse"><div className="h-4 bg-gray-200 rounded w-1/2 mx-auto mb-4" /><div className="h-4 bg-gray-200 rounded w-1/3 mx-auto" /></div>
            : !analytics ? <div className="p-8 text-center text-gray-500">{isHindi ? 'डेटा नहीं मिला' : 'No data'}</div>
            : (
              <>
                <div className="bg-white border border-gray-200 p-4">
                  <p className="text-sm text-gray-500">{isHindi ? 'कुल दृश्य (प्रकाशित लेख)' : 'Total Views (Published Articles)'}</p>
                  <p className="text-4xl font-bold text-[#121212]">{analytics.total_views?.toLocaleString()}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Top Articles */}
                  <div className="bg-white border border-gray-200 p-4">
                    <h3 className={`font-bold text-[#121212] mb-3 ${isHindi ? 'font-hindi-heading' : 'font-heading'}`}>
                      {isHindi ? 'शीर्ष लेख' : 'Top Articles'}
                    </h3>
                    <div className="space-y-2">
                      {analytics.topArticles?.map((a, i) => (
                        <div key={a.article_id} className="flex items-center gap-3">
                          <span className="text-xs font-bold text-gray-400 w-4">{i + 1}</span>
                          <div className="flex-1 min-w-0">
                            <Link to={`/article/${a.article_id}`} className="text-sm font-semibold text-gray-900 hover:text-[#121212] line-clamp-1">{a.title}</Link>
                            <p className="text-xs text-gray-500">{a.author_name} · <span className="category-pill text-xs py-0">{a.category}</span></p>
                          </div>
                          <span className="text-sm font-bold text-[#121212] shrink-0">{a.views?.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Views by Category */}
                  <div className="bg-white border border-gray-200 p-4">
                    <h3 className={`font-bold text-[#121212] mb-3 ${isHindi ? 'font-hindi-heading' : 'font-heading'}`}>
                      {isHindi ? 'श्रेणी अनुसार दृश्य' : 'Views by Category'}
                    </h3>
                    <div className="space-y-2">
                      {analytics.viewsByCategory?.map(c => {
                        const pct = analytics.total_views > 0 ? Math.round((c.total_views / analytics.total_views) * 100) : 0;
                        return (
                          <div key={c.category}>
                            <div className="flex justify-between text-sm mb-0.5">
                              <span className="capitalize font-semibold">{c.category}</span>
                              <span className="text-gray-500">{c.total_views?.toLocaleString()} ({pct}%)</span>
                            </div>
                            <div className="w-full bg-gray-100 rounded-full h-1.5">
                              <div className="bg-[#121212] h-1.5 rounded-full" style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Reporter Leaderboard */}
                  <div className="bg-white border border-gray-200 p-4">
                    <h3 className={`font-bold text-[#121212] mb-3 ${isHindi ? 'font-hindi-heading' : 'font-heading'}`}>
                      {isHindi ? 'रिपोर्टर लीडरबोर्ड' : 'Reporter Leaderboard'}
                    </h3>
                    <div className="space-y-2">
                      {analytics.reporterLeaderboard?.map((r, i) => (
                        <div key={r.author_id} className="flex items-center gap-3">
                          <span className={`text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center ${i === 0 ? 'bg-[#121212] text-white' : i === 1 ? 'bg-gray-300 text-gray-700' : i === 2 ? 'bg-orange-300 text-orange-800' : 'bg-gray-100 text-gray-500'}`}>{i + 1}</span>
                          <span className="flex-1 text-sm font-semibold text-gray-900">{r.author_name}</span>
                          <span className="text-xs text-gray-500">{r.article_count} {isHindi ? 'लेख' : 'articles'}</span>
                          <span className="text-sm font-bold text-[#121212]">{r.total_views?.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Articles over time */}
                  <div className="bg-white border border-gray-200 p-4">
                    <h3 className={`font-bold text-[#121212] mb-3 ${isHindi ? 'font-hindi-heading' : 'font-heading'}`}>
                      {isHindi ? 'पिछले 30 दिन (लेख)' : 'Last 30 Days (Articles Published)'}
                    </h3>
                    {analytics.articlesOverTime?.length === 0
                      ? <p className="text-sm text-gray-400">{isHindi ? 'कोई डेटा नहीं' : 'No data'}</p>
                      : (
                        <div className="flex items-end gap-1 h-24">
                          {analytics.articlesOverTime?.map(d => {
                            const max = Math.max(...analytics.articlesOverTime.map(x => x.count));
                            const h = max > 0 ? Math.max(4, Math.round((d.count / max) * 96)) : 4;
                            return (
                              <div key={d.date} className="flex-1 flex flex-col items-center gap-1" title={`${d.date}: ${d.count}`}>
                                <div className="w-full bg-[#121212] rounded-t" style={{ height: h }} />
                              </div>
                            );
                          })}
                        </div>
                      )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* ── TOP STORIES TAB (read-only: the ranking explains itself) ── */}
        {false && activeTab === 'top' && (
          <div>
            <p className={`text-sm text-gray-600 mb-4 ${isHindi ? 'font-hindi' : ''}`}>
              {isHindi
                ? 'मुख्य खबरें अपने आप चुनी जाती हैं, केवल यूपी की खबरों में से: कितने समाचार स्रोतों ने खबर दी, स्रोत कितना भरोसेमंद है, ब्रेकिंग फ़ीड, प्रदेश-स्तर या ज़िला और पाठक। स्कोर हर 6 घंटे में आधा हो जाता है।'
                : 'Top Stories are picked automatically from UP news using statewide or district reach and reader activity. Scores halve every 6 hours.'}
              {topStories?.computed_at && (
                <span className="text-gray-400"> {isHindi ? 'गणना:' : 'Calculated'} {fmtDate(topStories.computed_at)}</span>
              )}
            </p>
            <section className="bg-white border border-gray-200 p-4 mb-5">
              <div className="flex flex-wrap items-start justify-between gap-2 mb-4">
                <div>
                  <h3 className={`font-bold text-[#121212] ${isHindi ? 'font-hindi-heading' : 'font-heading'}`}>
                    {isHindi ? 'पिन की गई खबरों का क्रम' : 'Pinned story priority'}
                  </h3>
                  <p className={`text-sm text-gray-500 mt-1 ${isHindi ? 'font-hindi' : ''}`}>
                    {isHindi ? 'नई पिन की गई खबर सूची के अंत में जुड़ती है। क्रम बदलने पर बीच की खबरें अपने आप खिसकेंगी।' : 'New pins are added at the end. Changing a number shifts the stories between positions automatically.'}
                  </p>
                </div>
                <span className="text-xs text-gray-500">{pinnedArticles.length} {isHindi ? 'पिन की गईं' : 'pinned'}</span>
              </div>
              {loading ? (
                <div className="space-y-2 animate-pulse" aria-label={isHindi ? 'पिन की गई खबरें लोड हो रही हैं' : 'Loading pinned stories'}>
                  {[0, 1, 2].map((item) => <div key={item} className="h-12 rounded-sm bg-gray-100" />)}
                </div>
              ) : pinnedArticles.length === 0 ? (
                <p className="py-3 text-sm text-gray-500">{isHindi ? 'अभी कोई खबर पिन नहीं है। लेख सूची में पिन बटन से खबर जोड़ें।' : 'No stories are pinned yet. Use the pin button in the articles list to add one.'}</p>
              ) : (
                <ol className="divide-y divide-gray-100">
                  {pinnedArticles.map((article) => (
                    <li key={article.article_id} className="flex flex-wrap items-center justify-between gap-3 py-3">
                      <div className="flex min-w-0 items-center gap-3">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#121212] text-sm font-bold text-white">{article.pin_priority}</span>
                        <Link to={`/article/${article.article_id}`} className={`min-w-0 line-clamp-2 text-sm font-semibold text-gray-900 hover:underline ${isHindi ? 'font-hindi' : ''}`}>
                          {isHindi && article.title_hi ? article.title_hi : article.title}
                        </Link>
                      </div>
                      <label className="flex shrink-0 items-center gap-2 text-xs text-gray-600">
                        <span>{isHindi ? 'स्थान' : 'Position'}</span>
                        <select
                          value={article.pin_priority}
                          onChange={handlePinPriorityChange(article)}
                          aria-label={`${isHindi ? 'पिन क्रम' : 'Pin position'}: ${article.title}`}
                          className="border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900"
                        >
                          {pinnedArticles.map((_, index) => <option key={index + 1} value={index + 1}>{index + 1}</option>)}
                        </select>
                      </label>
                    </li>
                  ))}
                </ol>
              )}
            </section>
            <div className="bg-white border border-gray-200 overflow-x-auto">
              {loading || !topStories ? <div className="p-8 animate-pulse"><div className="h-4 bg-gray-200 rounded w-1/2 mx-auto" /></div>
              : topStories.candidates.length === 0 ? <div className="p-8 text-center text-gray-500">{isHindi ? 'पिछले 24 घंटों में कोई खबर नहीं' : 'No stories in the last 24 hours'}</div>
              : (
                <table className="data-table">
                  <thead><tr>
                    <th>{isHindi ? 'स्थिति' : 'Status'}</th>
                    <th>{isHindi ? 'स्कोर' : 'Score'}</th>
                    <th>{isHindi ? 'खबर' : 'Story'}</th>
                    <th>{isHindi ? 'स्रोत' : 'Outlets'}</th>
                    <th>{isHindi ? 'अंक' : 'Points'}</th>
                    <th>{isHindi ? 'उम्र' : 'Age'}</th>
                  </tr></thead>
                  <tbody>
                    {topStories.candidates.map((c, i) => (
                      <tr key={c.article_id || i} className={c.status === 'picked' || c.status === 'pinned' ? '' : 'opacity-60'}>
                        <td>
                          <span className={`text-xs font-bold uppercase px-2 py-1 rounded whitespace-nowrap ${
                            c.status === 'picked' || c.status === 'pinned' ? 'bg-[#121212] text-white' : 'bg-gray-100 text-gray-600'}`}>
                            {c.status}
                          </span>
                        </td>
                        <td className="font-bold text-gray-900">{c.score}</td>
                        <td className="max-w-md">
                          <Link to={`/article/${c.article_id}`} className="font-semibold text-gray-900 line-clamp-2 hover:underline">{c.title}</Link>
                          <span className="text-xs text-gray-500">
                            {c.category} · {c.reach}{c.breaking ? ' · breaking' : ''} · {c.articles_in_event} {c.articles_in_event === 1 ? 'article' : 'articles'}
                          </span>
                        </td>
                        <td className="text-xs text-gray-600 max-w-[14rem]">
                          <span className="font-bold text-gray-900">{c.sources.length}</span> — {c.sources.join(', ')}
                        </td>
                        <td className="text-xs text-gray-600 whitespace-nowrap">
                          {isHindi ? 'कवरेज' : 'coverage'} {c.points.coverage} · {isHindi ? 'भरोसा' : 'trust'} {c.points.trust}<br />
                          {isHindi ? 'ब्रेकिंग' : 'breaking'} {c.points.breaking} · {isHindi ? 'पहुंच' : 'reach'} {c.points.reach} · {isHindi ? 'पाठक' : 'readers'} {c.points.engagement}<br />
                          × {c.freshness} {isHindi ? 'ताज़गी' : 'freshness'}
                        </td>
                        <td className="text-xs text-gray-500 whitespace-nowrap">{c.age_hours}h</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* ── AUDIT LOG TAB ── */}
        {activeTab === 'audit' && (
          <div className="bg-white border border-gray-200 overflow-x-auto">
            {loading ? <div className="p-8 animate-pulse"><div className="h-4 bg-gray-200 rounded w-1/2 mx-auto" /></div>
            : auditLogs.length === 0 ? <div className="p-8 text-center"><Activity className="w-12 h-12 text-gray-300 mx-auto mb-4" /><p className="text-gray-500">{isHindi ? 'कोई लॉग नहीं' : 'No audit logs yet'}</p></div>
            : (
              <table className="data-table">
                <thead><tr>
                  <th>{isHindi ? 'कार्रवाई' : 'Action'}</th>
                  <th>{isHindi ? 'प्रकार' : 'Entity'}</th>
                  <th>{isHindi ? 'लेबल' : 'Label'}</th>
                  <th>{isHindi ? 'किसने किया' : 'Performed By'}</th>
                  <th>{isHindi ? 'विवरण' : 'Details'}</th>
                  <th>{isHindi ? 'समय' : 'Time'}</th>
                </tr></thead>
                <tbody>
                  {auditLogs.map(log => (
                    <tr key={log.id}>
                      <td><code className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-800">{log.action}</code></td>
                      <td><span className="text-xs text-gray-500">{log.entity_type}</span></td>
                      <td className="text-sm text-gray-800 max-w-xs truncate">{log.entity_label || '—'}</td>
                      <td className="text-sm font-semibold text-gray-800">{log.performed_by_name}</td>
                      <td className="text-xs text-gray-500">{log.details || '—'}</td>
                      <td className="text-xs text-gray-500 whitespace-nowrap">{fmtDate(log.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

      </div>

      {/* ── DIALOGS ── */}

      <AlertDialog
        open={pinPriorityDialog.open}
        onOpenChange={(open) => {
          if (!open) setPinPriorityDialog({ open: false, articleId: null, title: '', currentPriority: null, nextPriority: null });
        }}
      >
        <AlertDialogContent className="w-[calc(100vw-2rem)] max-w-lg overflow-visible">
          <AlertDialogHeader className="min-w-0">
            <AlertDialogTitle className={isHindi ? 'font-hindi-heading' : 'font-heading'}>
              {isHindi ? 'पिन क्रम बदलें?' : 'Change pinned story priority?'}
            </AlertDialogTitle>
            <AlertDialogDescription className={isHindi ? 'font-hindi' : ''}>
              <span className="block font-semibold text-[var(--ink)]">{pinPriorityDialog.title}</span>
              {isHindi
                ? `क्या आप इसे क्रम ${pinPriorityDialog.currentPriority} से ${pinPriorityDialog.nextPriority} पर ले जाना चाहते हैं? बीच की खबरों का क्रम अपने आप बदल जाएगा।`
                : `Move this story from position ${pinPriorityDialog.currentPriority} to ${pinPriorityDialog.nextPriority}? The stories between them will shift automatically.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{isHindi ? 'रद्द करें' : 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmPinPriorityChange} className="bg-[#121212] hover:bg-black">
              {isHindi ? 'क्रम बदलें' : 'Confirm change'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={tickerLimitAlert} onOpenChange={setTickerLimitAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className={isHindi ? 'font-hindi-heading' : 'font-heading'}>
              {isHindi ? 'सभी 5 टिकर स्लॉट भरे हैं' : 'All 5 ticker slots are full'}
            </AlertDialogTitle>
            <AlertDialogDescription className={isHindi ? 'font-hindi' : ''}>
              {isHindi
                ? 'नया ब्रेकिंग न्यूज़ टिकर जोड़ने के लिए किसी पुराने टिकर को हटाएँ या बंद करें।'
                : 'Delete or hide an old ticker to add a new breaking news ticker.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setTickerLimitAlert(false)} className="bg-red-600 hover:bg-red-700">
              {isHindi ? 'ठीक है' : 'OK'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={editingBreakingId !== null}
        onOpenChange={(open) => {
          if (!open) setEditingBreakingId(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className={isHindi ? 'font-hindi-heading' : 'font-heading'}>
              {isHindi ? 'टिकर संपादित करें' : 'Edit ticker'}
            </AlertDialogTitle>
            <AlertDialogDescription className={isHindi ? 'font-hindi' : ''}>
              {isHindi ? 'शीर्षक और वैकल्पिक लेख लिंक अपडेट करें।' : 'Update the headline and optional attached article.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="w-full min-w-0 space-y-3">
            <textarea
              value={editingBreakingText}
              onChange={(event) => setEditingBreakingText(event.target.value)}
              rows={4}
              maxLength={2000}
              aria-label={isHindi ? 'टिकर शीर्षक' : 'Ticker headline'}
              className={`block box-border w-full min-w-0 max-w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#121212] ${isHindi ? 'font-hindi' : ''}`}
            />
            <div className="relative w-full min-w-0">
              <button
                type="button"
                onClick={() => {
                  setEditingBreakingArticlePickerOpen((open) => !open);
                  setEditingBreakingArticleSearch('');
                }}
                aria-haspopup="listbox"
                aria-expanded={editingBreakingArticlePickerOpen}
                className="box-border w-full min-w-0 flex items-center justify-between gap-3 border border-gray-300 rounded-sm px-3 py-2 text-left text-sm bg-white focus:outline-none focus:border-[#121212]"
              >
                <span className="truncate">
                  {editingBreakingArticleId
                    ? (() => {
                      const selectedArticle = breakingArticleOptions.find((article) => article.article_id === editingBreakingArticleId);
                      return selectedArticle
                        ? ((isHindi && selectedArticle.title_hi) || selectedArticle.title)
                        : editingBreakingArticleId;
                    })()
                    : (isHindi ? 'कोई लेख संलग्न न करें' : 'No article attached')}
                </span>
                <ChevronDown className="w-4 h-4 shrink-0 text-gray-500" />
              </button>
              {editingBreakingArticlePickerOpen && (
                <div className="absolute z-50 mt-1 box-border w-full min-w-0 border border-gray-300 bg-white shadow-lg">
                  <label className="relative block p-2 border-b border-gray-200">
                    <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="search"
                      autoFocus
                      value={editingBreakingArticleSearch}
                      onChange={(event) => setEditingBreakingArticleSearch(event.target.value)}
                      placeholder={isHindi ? 'खबर खोजें...' : 'Search articles...'}
                      aria-label={isHindi ? 'खबर खोजें' : 'Search articles'}
                      className="w-full border border-gray-300 rounded-sm pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-[#121212]"
                    />
                  </label>
                  <div role="listbox" className="max-h-56 overflow-y-auto py-1">
                    <button
                      type="button"
                      role="option"
                      aria-selected={!editingBreakingArticleId}
                      onClick={() => {
                        setEditingBreakingArticleId('');
                        setEditingBreakingArticlePickerOpen(false);
                        setEditingBreakingArticleSearch('');
                      }}
                      className="w-full px-3 py-2 text-left text-sm text-gray-600 hover:bg-gray-100"
                    >
                      {isHindi ? 'कोई लेख संलग्न न करें' : 'No article attached'}
                    </button>
                    {filteredEditingBreakingArticleOptions.map((article) => (
                      <button
                        key={article.article_id}
                        type="button"
                        role="option"
                        aria-selected={editingBreakingArticleId === article.article_id}
                        onClick={() => {
                          setEditingBreakingArticleId(article.article_id);
                          setEditingBreakingArticlePickerOpen(false);
                          setEditingBreakingArticleSearch('');
                        }}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 ${isHindi ? 'font-hindi' : ''}`}
                      >
                        {(isHindi && article.title_hi) || article.title}
                      </button>
                    ))}
                    {filteredEditingBreakingArticleOptions.length === 0 && (
                      <p className="px-3 py-3 text-sm text-gray-500">
                        {isHindi ? 'कोई खबर नहीं मिली' : 'No articles found'}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          <AlertDialogFooter className="min-w-0">
            <AlertDialogCancel>{isHindi ? 'रद्द करें' : 'Cancel'}</AlertDialogCancel>
            <Button onClick={() => handleUpdateBreaking(editingBreakingId)} disabled={!editingBreakingText.trim()}>
              {isHindi ? 'सहेजें' : 'Save changes'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={tickerActionDialog.open}
        onOpenChange={(open) => {
          if (!open) setTickerActionDialog({ open: false, type: null, id: null, nextActive: null });
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className={isHindi ? 'font-hindi-heading' : 'font-heading'}>
              {tickerActionDialog.type === 'delete'
                ? (isHindi ? 'क्या आप इस टिकर को हटाना चाहते हैं?' : 'Are you sure you want to delete this ticker?')
                : tickerActionDialog.nextActive
                  ? (isHindi ? 'क्या आप इस टिकर को सक्रिय करना चाहते हैं?' : 'Are you sure you want to activate this ticker?')
                  : (isHindi ? 'क्या आप इस टिकर को निष्क्रिय करना चाहते हैं?' : 'Are you sure you want to deactivate this ticker?')}
            </AlertDialogTitle>
            <AlertDialogDescription className={isHindi ? 'font-hindi' : ''}>
              {tickerActionDialog.type === 'delete'
                ? (isHindi ? 'इसे हटाने के बाद वापस नहीं लाया जा सकेगा।' : 'This ticker will be permanently deleted.')
                : (isHindi ? 'पुष्टि करने पर टिकर की स्थिति बदल जाएगी।' : 'Confirm to change this ticker’s status.')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{isHindi ? 'रद्द करें' : 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmTickerAction}
              className={tickerActionDialog.type === 'delete' ? 'bg-red-600 hover:bg-red-700' : 'bg-[#121212] hover:bg-black'}
            >
              {tickerActionDialog.type === 'delete'
                ? (isHindi ? 'हटाएँ' : 'Delete')
                : tickerActionDialog.nextActive
                  ? (isHindi ? 'सक्रिय करें' : 'Activate')
                  : (isHindi ? 'निष्क्रिय करें' : 'Deactivate')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Article action dialog */}
      <AlertDialog open={actionDialog.open} onOpenChange={() => setActionDialog({ open: false, type: null, articleId: null, label: '' })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className={isHindi ? 'font-hindi-heading' : 'font-heading'}>
              {actionDialog.type === 'delete'    && (isHindi ? 'लेख हटाएं?' : 'Delete Article?')}
              {actionDialog.type === 'revoke'    && (isHindi ? 'लेख रद्द करें?' : 'Revoke Article?')}
              {actionDialog.type === 'republish' && (isHindi ? 'पुनः प्रकाशित करें?' : 'Republish Article?')}
              {actionDialog.type === 'pin'       && (isHindi ? 'पिन टॉगल करें?' : 'Toggle Pin?')}
            </AlertDialogTitle>
            <AlertDialogDescription className={isHindi ? 'font-hindi' : ''}>
              <span className="font-semibold">"{actionDialog.label}"</span>
              {actionDialog.type === 'delete'    && (isHindi ? ' — यह क्रिया पूर्ववत नहीं की जा सकती।' : ' — This cannot be undone.')}
              {actionDialog.type === 'revoke'    && (isHindi ? ' — लेख सार्वजनिक दृश्य से हट जाएगा।' : ' — This will remove it from public view.')}
              {actionDialog.type === 'republish' && (isHindi ? ' — लेख पुनः प्रकाशित होगा।' : ' — This will make it publicly visible again.')}
              {actionDialog.type === 'pin'       && (isHindi ? ' — पिन स्थिति बदलेगी।' : ' — Pin status will be toggled.')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{isHindi ? 'रद्द करें' : 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => articleAction(actionDialog.type, actionDialog.articleId, actionDialog.label)}
              className={actionDialog.type === 'delete' ? 'bg-red-600 hover:bg-red-700' : actionDialog.type === 'revoke' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-[#121212] hover:bg-[#000000]'}>
              {actionDialog.type === 'delete'    && (isHindi ? 'हटाएं' : 'Delete')}
              {actionDialog.type === 'revoke'    && (isHindi ? 'रद्द करें' : 'Revoke')}
              {actionDialog.type === 'republish' && (isHindi ? 'प्रकाशित करें' : 'Republish')}
              {actionDialog.type === 'pin'       && (isHindi ? 'टॉगल करें' : 'Toggle')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reporter approve/reject dialog */}
      <AlertDialog open={reporterDialog.open} onOpenChange={() => setReporterDialog({ open: false, type: null, reporter: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className={isHindi ? 'font-hindi-heading' : 'font-heading'}>
              {reporterDialog.type === 'approve' ? (isHindi ? 'रिपोर्टर स्वीकृत करें?' : 'Approve Reporter?') : (isHindi ? 'रिपोर्टर अस्वीकृत करें?' : 'Reject Reporter?')}
            </AlertDialogTitle>
            <AlertDialogDescription className={isHindi ? 'font-hindi' : ''}>
              {reporterDialog.type === 'approve'
                ? (isHindi ? `${reporterDialog.reporter?.name} को लेख प्रकाशित करने की अनुमति मिलेगी।` : `${reporterDialog.reporter?.name} will be allowed to publish articles.`)
                : (isHindi ? `${reporterDialog.reporter?.name} लेख प्रकाशित नहीं कर पाएंगे।` : `${reporterDialog.reporter?.name} will not be able to publish articles.`)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{isHindi ? 'रद्द करें' : 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction onClick={handleReporterAction}
              className={reporterDialog.type === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}>
              {reporterDialog.type === 'approve' ? (isHindi ? 'स्वीकृत करें' : 'Approve') : (isHindi ? 'अस्वीकृत करें' : 'Reject')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Category delete dialog */}
      <AlertDialog open={categoryDialog.open} onOpenChange={() => setCategoryDialog({ open: false, category: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className={isHindi ? 'font-hindi-heading' : 'font-heading'}>{isHindi ? 'श्रेणी हटाएं?' : 'Delete Category?'}</AlertDialogTitle>
            <AlertDialogDescription className={isHindi ? 'font-hindi' : ''}>
              {isHindi ? `"${categoryDialog.category?.name}" हटाई जाएगी। यदि कोई लेख इसका उपयोग कर रहा है तो यह नहीं हो सकता।` : `"${categoryDialog.category?.name}" will be deleted. Cannot delete if articles are using it.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{isHindi ? 'रद्द करें' : 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteCategory} className="bg-red-600 hover:bg-red-700">{isHindi ? 'हटाएं' : 'Delete'}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* User action dialog */}
      <AlertDialog open={userDialog.open} onOpenChange={() => setUserDialog({ open: false, type: null, user: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className={isHindi ? 'font-hindi-heading' : 'font-heading'}>
              {userDialog.type === 'edit' && (isHindi ? 'उपयोगकर्ता संपादित करें' : 'Edit User')}
              {userDialog.type === 'create' && (isHindi ? 'नया उपयोगकर्ता' : 'Create User')}
              {userDialog.type === 'activate' && (isHindi ? 'उपयोगकर्ता सक्रिय करें?' : 'Activate User?')}
              {userDialog.type === 'deactivate' && (isHindi ? 'उपयोगकर्ता निष्क्रिय करें?' : 'Deactivate User?')}
            </AlertDialogTitle>
            <AlertDialogDescription className={isHindi ? 'font-hindi' : ''}>
              {(userDialog.type === 'edit' || userDialog.type === 'create') ? (
                <div className="space-y-3 mt-2">
                  <label className="block text-sm font-semibold">{isHindi ? 'नाम' : 'Name'}
                    <input required maxLength={255} value={userForm.name} onChange={e => setUserForm(p => ({ ...p, name: e.target.value }))} className="mt-1 w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                  </label>
                  <label className="block text-sm font-semibold">Email
                    <input required type="email" maxLength={254} value={userForm.email} onChange={e => setUserForm(p => ({ ...p, email: e.target.value }))} className="mt-1 w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                  </label>
                  <label className="block text-sm font-semibold">{isHindi ? 'मोबाइल' : 'Phone'}
                    <input type="tel" value={userForm.phone} onChange={e => setUserForm(p => ({ ...p, phone: e.target.value }))} placeholder="10-digit mobile (optional)" className="mt-1 w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                  </label>
                  <label className="block text-sm font-semibold">{isHindi ? 'भूमिका' : 'Role'}
                    <select value={userForm.role} onChange={e => setUserForm(p => ({ ...p, role: e.target.value }))} className="mt-1 w-full border border-gray-300 rounded px-3 py-2 text-sm">
                      <option value="reporter">Reporter</option><option value="admin">Admin</option>
                      {userDialog.user?.role === 'reader' && <option value="reader">Reader</option>}
                    </select>
                  </label>
                  {userDialog.type === 'create' && <label className="block text-sm font-semibold">{isHindi ? 'पासवर्ड' : 'Temporary password'}
                    <span className="relative mt-1 block">
                      <input required type={showTemporaryPassword ? 'text' : 'password'} minLength={8} maxLength={72} value={userForm.password} onChange={e => setUserForm(p => ({ ...p, password: e.target.value }))} className="w-full border border-gray-300 rounded px-3 py-2 pr-10 text-sm" />
                      <button type="button" onClick={() => setShowTemporaryPassword(v => !v)} aria-label={showTemporaryPassword ? 'Hide password' : 'Show password'} aria-pressed={showTemporaryPassword} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-800">
                        {showTemporaryPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </span>
                    <span className="block text-xs font-normal text-gray-500 mt-1">At least 8 characters; share it with the user securely.</span>
                  </label>}
                </div>
              ) : <span className="font-semibold">{userDialog.user?.name}</span>}
              {userDialog.type === 'deactivate' && ' — This user will not be able to log in.'}
              {userDialog.type === 'activate' && ' — This user will be able to log in again.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{isHindi ? 'रद्द करें' : 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction onClick={handleUserAction}
              className={userDialog.type === 'deactivate' ? 'bg-red-600 hover:bg-red-700' : 'bg-[#121212] hover:bg-[#000000]'}>
              {(userDialog.type === 'edit' || userDialog.type === 'create') && (isHindi ? 'सहेजें' : 'Save')}
              {userDialog.type === 'activate' && (isHindi ? 'सक्रिय करें' : 'Activate')}
              {userDialog.type === 'deactivate' && (isHindi ? 'निष्क्रिय करें' : 'Deactivate')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


    </div>
  );
}
